// @flow

import { css } from 'styled-components';
import { breakpoint } from './responsive';
import type { BreakpointName } from './responsive';

const COLUMN_WIDTHS = [
  '8.333333333',
  '16.666666667',
  '25',
  '33.333333333',
  '41.666666667',
  '50',
  '58.333333333',
  '66.666666667',
  '75',
  '83.333333333',
  '91.666666667',
  '100',
];

const MAX_WIDTH = '1200px';
const GUTTER = '1.5rem';
const HALF_GUTTER = '0.75rem';
export const VERTICAL_SPACE = 6;
export const HORIZONTAL_SPACE = 12;

export function grid() {
  return css`
    margin: 0 auto;
    padding-left: ${GUTTER};
    padding-right: ${GUTTER};
    display: flex;
    flex-direction: column;
    max-width: ${MAX_WIDTH};
  `;
}

export function row() {
  return css`
    display: flex;
    flex: 0 1 auto;
    flex-direction: row;
    flex-wrap: wrap;
    margin-left: -${HALF_GUTTER};
    margin-right: -${HALF_GUTTER};
  `;
}

export type ColumnSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type ColumnWidth = ColumnSize | 'auto';
export type ColumnOffset = ColumnSize | null;
type ColumnArg = ColumnWidth | null;

const width = (name: BreakpointName, width: ColumnWidth) => {
  if (width === 'auto') {
    return breakpoint(name)`
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      flex-basis: 0;
      max-width: 100%;
    `;
  }
  return breakpoint(name)`
    display: flex;
    flex-direction: column;
    flex-basis: ${COLUMN_WIDTHS[width - 1]}%;
    max-width: ${COLUMN_WIDTHS[width - 1]}%;
  `;
};

const offset = (name: BreakpointName, offset: ColumnSize) => {
  return breakpoint(name)`
    margin-left: ${COLUMN_WIDTHS[offset - 1]}%;
  `;
};

export function column(
  small: ColumnArg = 'auto',
  medium: ColumnArg = null,
  large: ColumnArg = null,
  offsetSmall: ColumnOffset = null,
  offsetMedium: ColumnOffset = null,
  offsetLarge: ColumnOffset = null
) {
  return css`
    display: flex;
    flex: 0 0 auto;
    padding-left: ${HALF_GUTTER};
    padding-right: ${HALF_GUTTER};

    ${small !== null && width('small', small)};
    ${medium !== null && width('medium', medium)};
    ${large !== null && width('large', large)};

    ${offsetSmall !== null && offset('small', offsetSmall)};
    ${offsetMedium !== null && offset('medium', offsetMedium)};
    ${offsetLarge !== null && offset('large', offsetLarge)};
  `;
}

const vspace = (name: BreakpointName, offset: number) => {
  return breakpoint(name)`
    padding-top: ${offset * VERTICAL_SPACE}px; 
  `;
};

export function vspacer(
  small: number = 1,
  medium: number | null = null,
  large: number | null = null
) {
  return css`
    ${small !== null && vspace('small', small)};
    ${medium !== null && vspace('medium', medium)};
    ${large !== null && vspace('large', large)};
  `;
}

const hspace = (name: BreakpointName, offset: number) => {
  return breakpoint(name)`
    padding-left: ${offset * HORIZONTAL_SPACE}px; 
  `;
};

export function hspacer(
  small: number = 1,
  medium: number | null = null,
  large: number | null = null
) {
  return css`
    ${small !== null && hspace('small', small)};
    ${medium !== null && hspace('medium', medium)};
    ${large !== null && hspace('large', large)};
  `;
}
